import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  Col,
  Container,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { FaRegEye } from "react-icons/fa";
import CompanySelector from "ATSComponents/Common/CompanySelector";
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer";

const OptOutCandidates = () => {
  document.title = "ATS - Opt Out Candidates";

  const userInfos = JSON.parse(sessionStorage.getItem("user-info") || "{}");
  const initialCompanyId =
    userInfos?.user_details?.companies_data?.[0]?.company_id || null;

  const [companyId, setCompanyId] = useState(initialCompanyId);
  const [candidates, setCandidates] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null);
  const [ViewCandidatePopupShow, setViewCandidatePopupShow] = useState(false);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
  });

  const handleCompanyChange = company => {
    if (company?.company_id) {
      setCompanyId(company.company_id);
      setLazyParams(prev => ({ ...prev, first: 0, page: 0 }));
    }
  };

  useEffect(() => {
    if (!companyId) return;

    const fetchOptOutCandidates = async () => {
      setLoading(true);
      try {
        const response = await axios.get("/api/v1/opt-out-candidates/", {
          params: {
            company_id: companyId,
            page: lazyParams.page + 1,
            size: lazyParams.rows,
          },
        });

        const data = response.data || {};
        const rows = Array.isArray(data.results) ? data.results : [];

        setCandidates(rows);
        setTotalRecords(data.total_count ?? data.count ?? rows.length);
      } catch (error) {
        console.error("Error fetching opt out candidates:", error);
        setCandidates([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };

    fetchOptOutCandidates();
  }, [companyId, lazyParams]);

  const onPageChange = event => {
    setLazyParams(event);
  };

  const getCandidateName = rowData => {
    const firstLast = `${rowData?.first_name || ""} ${rowData?.last_name || ""}`.trim();
    return (
      rowData?.candidate_name ||
      rowData?.full_name ||
      rowData?.name ||
      firstLast ||
      "-"
    );
  };

  const getField = (rowData, fields, fallback = "-") => {
    for (const field of fields) {
      const value = rowData?.[field];
      if (value !== undefined && value !== null && value !== "") return value;
    }
    return fallback;
  };

  const formatExperience = exp => {
    if (!exp) return "-";
    const [years = 0, months = 0] = exp.toString().split(".").map(Number);
    return `${years || 0} Years ${months || 0} Months`;
  };

  const formatDateTime = value => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const ViewCandidateInfoCall = (candidate = null) => {
    if (candidate) setSelectedCandidateDetails(candidate);
    setViewCandidatePopupShow(prev => !prev);
  };

  const viewCandidateDetails = rowData => (
    <Button
      className="btn btn-light"
      onClick={() => ViewCandidateInfoCall(rowData)}
      title="View Candidate Details"
    >
      <FaRegEye />
    </Button>
  );

  const optOutStatusTemplate = () => (
    <span className="fw-semibold text-warning">Opted Out</span>
  );

  return (
    <Container fluid className="page-content">
      <div className="page-title-box mb-0 recruiter-dashboard">
        <Row className="align-items-center justify-content-between">
          <Col>
            <h1 className="page-title mb-4">Opt Out Candidates</h1>
          </Col>
          <Col xxl={3} xl={3} lg={3} md={4} sm={12} className="text-end">
            <FormGroup className="mb-0 text-start">
              <Label for="CompanySelect" className="fw-semibold d-block">
                Select Company <span className="text-danger">*</span>
              </Label>
              <CompanySelector
                onCompanyChange={handleCompanyChange}
                selectedCompanyId={companyId}
              />
            </FormGroup>
          </Col>
        </Row>

        {loading ? (
          <OrgTableShimmer columns={6} rows={7} titleWidth="210px" />
        ) : (
          <Card className="mb-3 shadow-md border-0 p-1">
            <DataTable
              value={candidates}
              dataKey="candidate_id"
              lazy
              paginator
              first={lazyParams.first}
              rows={lazyParams.rows}
              totalRecords={totalRecords}
              onPage={onPageChange}
              rowsPerPageOptions={[5, 10, 20, 50]}
              scrollable
              scrollHeight="500px"
              responsiveLayout="scroll"
              emptyMessage="No opt out candidates found."
              id="OptOutCandidates_TableView"
            >
              <Column header="Name" body={getCandidateName} />
              <Column
                header="Email"
                body={rowData => getField(rowData, ["email", "candidate_email"])}
              />
              <Column
                header="Phone"
                body={rowData =>
                  getField(rowData, ["mobile_phone", "phone", "candidate_phone"])
                }
              />
              <Column
                header="Job Title"
                body={rowData => getField(rowData, ["job_title", "position"])}
              />
              <Column
                header="Opted Out At"
                body={rowData =>
                  formatDateTime(
                    getField(rowData, ["opted_out_at", "opt_out_at", "created_at"], "")
                  )
                }
              />
              <Column header="Candidate Details" body={viewCandidateDetails} />
              <Column header="Status" body={optOutStatusTemplate} />
            </DataTable>
          </Card>
        )}
      </div>

      <Modal
        isOpen={ViewCandidatePopupShow}
        toggle={() => ViewCandidateInfoCall()}
        size="xl"
        className="candidate-modal"
      >
        <ModalHeader
          toggle={() => ViewCandidateInfoCall()}
          className="bg-light border-0"
        >
          <h4 className="mb-0 text-dark fw-semibold">
            {getCandidateName(selectedCandidateDetails)}
          </h4>
        </ModalHeader>
        <ModalBody
          style={{
            maxHeight: "75vh",
            overflowY: "auto",
            padding: "1.5rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "12px",
          }}
        >
          <Card
            body
            style={{
              border: "none",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              borderRadius: "16px",
              backgroundColor: "#ffffff",
            }}
          >
            <Row className="g-4">
              {[
                ["Name", getCandidateName(selectedCandidateDetails)],
                [
                  "Total Experience",
                  formatExperience(selectedCandidateDetails?.total_experience),
                ],
                [
                  "Email",
                  getField(selectedCandidateDetails, ["email", "candidate_email"]),
                ],
                [
                  "Phone",
                  getField(selectedCandidateDetails, [
                    "mobile_phone",
                    "phone",
                    "candidate_phone",
                  ]),
                ],
                [
                  "Current Company",
                  getField(selectedCandidateDetails, ["current_company"]),
                ],
                [
                  "Designation",
                  getField(selectedCandidateDetails, ["designation"]),
                ],
                [
                  "Job Title",
                  getField(selectedCandidateDetails, ["job_title", "position"]),
                ],
                [
                  "Opted Out At",
                  formatDateTime(
                    getField(
                      selectedCandidateDetails,
                      ["opted_out_at", "opt_out_at", "created_at"],
                      ""
                    )
                  ),
                ],
              ].map(([label, value], idx) => (
                <Col md={6} key={idx}>
                  <div
                    style={{
                      border: "1px solid #dee2e6",
                      borderRadius: "10px",
                      padding: "0.9rem 1rem",
                      backgroundColor: "#fdfdfd",
                    }}
                  >
                    <strong style={{ color: "#6c757d" }}>{label}:</strong>
                    <span style={{ color: "#212529", fontWeight: 500 }}>
                      {" "}
                      {value || "-"}
                    </span>
                  </div>
                </Col>
              ))}
              <Col md={12}>
                <div
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "10px",
                    padding: "0.9rem 1rem",
                    backgroundColor: "#fdfdfd",
                  }}
                >
                  <strong style={{ color: "#6c757d" }}>Reason:</strong>
                  <span style={{ color: "#212529", marginLeft: "0.5rem" }}>
                    {getField(selectedCandidateDetails, [
                      "reason",
                      "opt_out_reason",
                      "comments",
                    ])}
                  </span>
                </div>
              </Col>
            </Row>
          </Card>
        </ModalBody>
        <ModalFooter className="border-0 justify-content-center">
          <Button
            color="secondary"
            onClick={() => ViewCandidateInfoCall()}
            className="px-4 blueBtn"
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default OptOutCandidates;
